import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  Image,
  File,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { uploadAPI } from '../../services/api';
import { toast } from 'sonner';

const FILE_TYPE_ICONS = {
  'image/': Image,
  'application/pdf': FileText,
  'application/msword': FileText,
  'application/vnd.openxmlformats': FileText,
  'text/': FileText,
  default: File,
};

const getFileIcon = type => {
  for (const [key, Icon] of Object.entries(FILE_TYPE_ICONS)) {
    if (type.startsWith(key)) return Icon;
  }
  return FILE_TYPE_ICONS.default;
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function FileUpload({
  onUploadComplete,
  maxFiles = 5,
  maxSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif',
  folder = 'documents',
  multiple = true,
  className = '',
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter(file => {
        if (file.size > maxSize) {
          toast.error(
            `File ${file.name} is too large. Max size is ${formatFileSize(
              maxSize
            )}`
          );
          return false;
        }
        return true;
      });

      if (files.length + validFiles.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const newFiles = validFiles.map(file => ({
        file,
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'pending',
        progress: 0,
        url: null,
      }));

      setFiles(prev => [...prev, ...newFiles]);
    },
    [files.length, maxFiles, maxSize]
  );

  const handleFileSelect = e => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      if (file.size > maxSize) {
        toast.error(
          `File ${file.name} is too large. Max size is ${formatFileSize(
            maxSize
          )}`
        );
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newFiles = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      progress: 0,
      url: null,
    }));

    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = id => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const uploadFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.info('No files to upload');
      return;
    }

    setUploading(true);
    const uploadedFiles = [];

    for (const fileItem of pendingFiles) {
      try {
        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? { ...f, status: 'uploading', progress: 30 }
              : f
          )
        );

        const result = await uploadAPI.uploadFile(fileItem.file, folder);

        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? {
                  ...f,
                  status: 'completed',
                  progress: 100,
                  url: result.url,
                  publicId: result.publicId,
                }
              : f
          )
        );

        uploadedFiles.push({
          name: fileItem.name,
          url: result.url,
          publicId: result.publicId,
          type: fileItem.type,
          size: fileItem.size,
        });
      } catch (error) {
        console.error('Upload failed:', error);
        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id ? { ...f, status: 'error', progress: 0 } : f
          )
        );
        toast.error(`Failed to upload ${fileItem.name}`);
      }
    }

    setUploading(false);

    if (uploadedFiles.length > 0 && onUploadComplete) {
      onUploadComplete(uploadedFiles);
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className={className} data-testid="file-upload">
      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <p className="text-gray-700 font-medium">
              Drag and drop files here, or{' '}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-primary hover:underline"
              >
                browse
              </button>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Max {maxFiles} files, up to {formatFileSize(maxSize)} each
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-3">
          {files.map(fileItem => {
            const FileIcon = getFileIcon(fileItem.type);

            return (
              <div
                key={fileItem.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                data-testid={`file-item-${fileItem.id}`}
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
                  <FileIcon className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.name}
                    </p>
                    {getStatusIcon(fileItem.status)}
                  </div>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(fileItem.size)}
                  </p>
                  {fileItem.status === 'uploading' && (
                    <Progress value={fileItem.progress} className="h-1 mt-1" />
                  )}
                </div>
                {fileItem.status !== 'uploading' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(fileItem.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}

          {/* Upload Button */}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setFiles([])}
              disabled={uploading}
            >
              Clear All
            </Button>
            <Button
              onClick={uploadFiles}
              disabled={
                uploading ||
                files.filter(f => f.status === 'pending').length === 0
              }
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
